import React from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import { MdEmail } from "react-icons/md";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.magnirauniverse.com";

const EmailHelper = ({
  items,
  clientName,
  clientDetails,
  discountPercentage,
  calculateTotal,
}) => {
  const handleSendMail = async () => {
    if (!clientDetails || !clientDetails.email) {
      alert("❌ Client email not found!");
      return;
    }

    try {
      const rawName = localStorage.getItem("userName");
      const loginUserName =
        rawName && rawName.trim() !== "" ? rawName : "Team SVB";
      const loginUserEmail = localStorage.getItem("userEmail")?.trim();

      const pdfBlob = await exportToPDFAndSendMail();

      const formData = new FormData();
      formData.append("toEmail", clientDetails.email);

      const validEmails = [
        "aim.sdcllp@gmail.com",
        "manan.sheth@partner.designcafe.com",
        loginUserEmail,
      ].filter((email) => email && email.includes("@"));

      formData.append("ccEmail", validEmails.join(","));

      const now = new Date();
      const timestamp = now.toLocaleString("en-IN").replace(/[\s/:]/g, "-");
      const projectSubject = `Site Service Quote for ${
        clientDetails?.projectName || "your home"
      } - ${timestamp}`;

      formData.append("subject", projectSubject);

      // ✅ Detect if it's an updated quote
      const isUpdatedQuote = !!clientDetails?.isUpdated;

      const messageBody = isUpdatedQuote
        ? `
          Dear ${clientName || "Client"},<br/><br/>
          We’re moving one step closer to making your dream home a reality! <br/><br/>

As per your recent discussion with our team, please find the updated Site Services quotation attached for your reference. <br/><br/>

We’re all set with the necessary tools and resources to begin your site measurement and 3D design process. Let’s take the next step and book your project with us!<br/><br/>

Looking forward to creating something beautiful together. <br/><br/>
          Thanks & Regards,<br/>
          <strong>${loginUserName}</strong><br/>
          SoftVolute Buildcon LLP<br/>
          (Site Services partner of DesignCafe)
        `
        : `
          Dear ${clientName || "Client"},<br/><br/>
          Thank you for contacting DesignCafe for your project. We are delighted to be a part of your journey towards creating your dream home.<br/><br/>

<span style="color: #5F259F;">
Your inquiry has been successfully processed, and the quotation will be shared in two parts:<br/>
1. Modular Part<br/>
2. Site Services Part
</span><br/><br/>

This email contains a Site Services quotation. <strong> Please find the detailed site services estimate attached, based on your recent discussion with our team. </strong>If you have any questions, please feel free to reach out to your assigned designer or sales manager. Kindly note that if any scope changes during Design stage and/or site validation stage, the final estimate may change as well. <br/><br/>

Let's connect with us to initiate the next step of booking, where as site measurement and 3D design will be followed after that. We look forward to moving ahead with your project as early as possible..<br/><br/>

Thanks & Regards,<br/>
<strong>${loginUserName}</strong><br/>
SoftVolute Buildcon LLP<br/>
(Site Services partner of DesignCafe)
        `;

      formData.append("message", messageBody);
      formData.append("attachment", pdfBlob, "SiteServiceQuote.pdf");

      console.log("✅ Sending Email To:", clientDetails.email);
      await axios.post(`${BASE_URL}/api/email/send-mail`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("✅ Mail Sent Successfully!");
    } catch (error) {
      console.error("❌ Error sending mail:", error);
      alert("❌ Failed to send mail, check console.");
    }
  };

  const exportToPDFAndSendMail = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const { totalMRP, projectManagementFee, discountAmount, finalValue } =
      calculateTotal();

    const roomSummaryData = Object.values(
      items.reduce((acc, item) => {
        if (item.room) {
          const discountedPrice =
            item.MRP - (item.MRP * discountPercentage) / 100;
          if (!acc[item.room]) {
            acc[item.room] = { roomName: item.room, cost: 0 };
          }
          acc[item.room].cost += discountedPrice || 0;
        }
        return acc;
      }, {}),
    );

    const drawFooter = () => {
      const footerHeight = 12;
      const footerY = pdfHeight - footerHeight;
      pdf.setFillColor(52, 58, 118);
      pdf.rect(0, footerY, pdfWidth, footerHeight, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.text(
        "SF 9 Time Square 2, Nr. Sindhu Bhavan Road, Thaltej, Ahmedabad-380059 | Mobile: 9998038595",
        pdfWidth / 2,
        pdfHeight - 4,
        { align: "center" },
      );
    };

    const renderDivToPDF = async (div) => {
      const canvas = await html2canvas(div, {
        scale: 2,
        backgroundColor: "#d3d3d3",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
      drawFooter();
    };

    // 🔥🔥🔥 Important Section Starts Here 🔥🔥🔥

    const sections = [
      createCoverPage(),
      ...createQuotePages(),
      createCostSummaryPage(),
      createRoomSummaryPage(),
      createNotesPage(),
    ];

    for (let i = 0; i < sections.length; i++) {
      if (i > 0) pdf.addPage();
      document.body.appendChild(sections[i]);
      await renderDivToPDF(sections[i]);
      document.body.removeChild(sections[i]);
    }

    const pdfBlob = await pdf.output("blob");
    return pdfBlob;
  };

  // 👇 All page creators
  const createCoverPage = () => {
    const div = document.createElement("div");
    div.style.width = "210mm";
    div.style.height = "297mm";
    div.style.background = "#d3d3d3";
    div.style.padding = "20mm";
    div.style.boxSizing = "border-box";
    div.style.fontFamily = "Calibri, sans-serif";
    div.innerHTML = `
    <div style="font-size:26px; font-weight:bold; color:#343A76;">SOFTVOLUTE BUILDCON LLP</div>
    <div style="text-align:center; font-size:36px; color:#343A76; margin-top:20px;">Site Service Quotation</div>
    <div style="text-align:center; margin-top:15px;">
      <div style="display:inline-block; padding:6px 12px; background:white; color:#343A76; font-size:16px; font-weight:500;">
        Keep Your Costs On Track For Your New Home Interiors
      </div>
    </div>
    <div style="margin: 50px 0 180px 0; font-size:16px; color:#343A76; line-height:1.7;">
      Dear <strong>${clientName}</strong> & Family!<br/><br/>
      Please go through our estimated price quote for your project.<br/>
      It may upgrade as per your design elevates.<br/><br/>
      Tracking of budget & time can be crucial for this kind of works.<br/>
      But with SVB's transparent and informed systems, client stay aware of project updates.<br/><br/>
      A Big Thank You for choosing SVB for your project & Welcome.
    </div>
    <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px;">
  <!-- Left side text -->
  <div style="
  font-family: 'Lucida Calligraphy', cursive;
  font-size: 26px;
  color: #343A76;
  font-weight: normal;
  max-width: 50%;
  padding-left: 40px;
">
  Home is the place where every corner tells your story ....
</div>


  <!-- Right side image -->
  <div style="padding-right: 30px;">
    <img src="/relaxing img-02.jpg" style="width: 280px; max-height: 180px;" />
  </div>
</div>

`;

    return div;
  };

  // Quote Table
  const createQuotePages = () => {
    const divs = [];
    const itemsPerPage = 10;
    const showDiscount = discountPercentage > 0;

    for (let i = 0; i < items.length; i += itemsPerPage) {
      const currentItems = items.slice(i, i + itemsPerPage);
      const div = document.createElement("div");

      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.background = "#d3d3d3";
      div.style.padding = `15mm`;
      div.style.boxSizing = "border-box";
      div.style.fontFamily = "Calibri, sans-serif";

      div.innerHTML = `
        <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
        <div style="font-size: 14px;">
          Client: ${clientName}<br/>
          Address: ${clientDetails?.clientAddress || "N/A"}
          <span style="float:right;">Date: ${new Date().toLocaleDateString(
            "en-IN",
          )}</span>
        </div>
  
        <h2 style="text-align:center; font-size: 20px; margin-top: 10px;">Quote Details</h2>
  
        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; background-color: #FFFFED; border: 0.5px solid black; border-radius: 8px; overflow: hidden;">
          <thead>
            <tr style="background-color: #707171; color: #FFFFED;">
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Sr No</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Image</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Item Name</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Room</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Price<br/>(INR)</th>
              ${
                showDiscount
                  ? `<th style="border: 0.5px solid lightgrey; padding: 8px;">Discounted Price</th>`
                  : ""
              }
            </tr>
          </thead>
          <tbody>
            ${currentItems
              .map((item, index) => {
                const originalPrice = item.MRP || 0;
                const discount =
                  (originalPrice * discountPercentage) / 100 || 0;
                const finalPrice = originalPrice - discount;

                return `
                <tr>
                  <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                    i + index + 1
                  }</td>
                  <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">
                    ${
                      item.image
                        ? `<img src="${item.image}" style="width: 80px; height: 50px; margin: 2px;" alt="${item.label}" />`
                        : ""
                    }
                  </td>
                  <td style="border: 0.5px solid lightgrey; padding: 8px;">
                    <strong>${item.label}</strong><br/>${item.description || ""}
                  </td>
                  <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                    item.room || "—"
                  }</td>
                  <td style="border: 0.5px solid lightgrey; text-align: right; padding: 8px;">
                    ${
                      showDiscount
                        ? `<span style="text-decoration: line-through; color: black; width:70px">₹ ${originalPrice.toLocaleString(
                            "en-IN",
                          )}</span>`
                        : `₹ ${originalPrice.toLocaleString("en-IN")}`
                    }
                  </td>
                  ${
                    showDiscount
                      ? `<td style="border: 0.5px solid lightgrey; text-align: right; padding: 8px; color: green; font-weight: bold; width:70px">
                          ₹ ${Math.round(finalPrice).toLocaleString("en-IN")}
                        </td>`
                      : ""
                  }
                </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      `;

      divs.push(div);
    }

    return divs;
  };

  // cost Summary Page
  const createCostSummaryPage = () => {
    const { totalMRP, discountAmount, projectManagementFee, finalValue } =
      calculateTotal(); // ✅ fetch totals here

    const showDiscount =
      discountPercentage > 0 && discountAmount > 0
        ? `
          <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
            <span style="color: green; font-weight: bold;">Total Discount (${discountPercentage}%):</span>
            <span style="color: green; font-weight: bold;">-₹ ${Math.round(
              discountAmount,
            ).toLocaleString("en-IN")}</span>
          </div>
          <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
        `
        : "";

    const div = document.createElement("div");
    div.style.width = "210mm";
    div.style.height = "297mm";
    div.style.background = "#d3d3d3";
    div.style.padding = "20mm";
    div.style.boxSizing = "border-box";
    div.style.fontFamily = "Calibri, sans-serif";
    const loginUserName =
      localStorage.getItem("userName")?.trim() || "Team SVB";

    div.innerHTML = `
      <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
      <div style="font-size: 14px; margin-bottom: 10px;">
        Client: ${clientName}<br/>
        Address: ${clientDetails.clientAddress || "N/A"}
        <span style="float:right;">Date: ${new Date().toLocaleDateString(
          "en-IN",
        )}</span>
      </div>
  
      <h2 style="text-align: center; font-size: 26px; font-weight: bold; color: #343A76; margin-top: 60px; margin-bottom: 30px;">
        Total Cost Breakup<br/>
        (Site Service)
      </h2>
  
      <div style="background-color: #fff3b0; border-radius: 25px; padding: 25px 35px; width: 80%; margin: 0 auto; box-shadow: 5px 8px 12px rgba(0, 0, 0, 0.08);">
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span>Total Article Value</span>
          <span>₹ ${Math.round(totalMRP).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
  
        ${showDiscount} <!-- ✅ dynamically inserted discount -->
  
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span>Project Management & Design Fees (9%)</span>
          <span>₹ ${Math.round(projectManagementFee).toLocaleString(
            "en-IN",
          )}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
  
        <div style="display: flex; justify-content: space-between; font-size: 22px; font-weight: bold; color: #343A76;">
          <span>Total</span>
          <span>₹ ${Math.round(finalValue).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.5px solid #6f4e37; margin: 10px 0 15px 0;" />
  
        <div style="font-size: 14px; color: #6f4e37; margin-top: 15px;">
          * Quote is inclusive of GST<br/>
          ** This quote will expire on 
          <strong>${new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}</strong>
        </div>
        <!-- ✅ Prepared by Name Bottom Right -->
<div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
  <strong>Prepared by:</strong> ${loginUserName}
  <br/>
  <span style="color: grey; font-size: 14px;">
    (If you have any questions about the quotation, feel free to reach out to your designer.)
  </span>
</div>

      </div>
    `;

    return div;
  };

  // ROom Summary Page
  const createRoomSummaryPage = () => {
    const div = document.createElement("div");
    div.style.width = "210mm";
    div.style.height = "297mm";
    div.style.background = "#d3d3d3";
    div.style.padding = "15mm";
    div.style.fontFamily = "Calibri, sans-serif";

    // ✅ Define Room Summary properly inside
    const roomSummaryData = Object.values(
      items.reduce((acc, item) => {
        if (item.room) {
          const discountedPrice =
            item.MRP - (item.MRP * discountPercentage) / 100;
          if (!acc[item.room]) {
            acc[item.room] = { roomName: item.room, cost: 0 };
          }
          acc[item.room].cost += discountedPrice || 0;
        }
        return acc;
      }, {}),
    );

    div.innerHTML = `
      <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
      <div style="font-size: 14px; margin-bottom: 10px;">
        Client: ${clientName}<br/>Address: ${
          clientDetails.clientAddress || "N/A"
        }
        <span style="float:right;">Date: ${new Date().toLocaleDateString(
          "en-IN",
        )}</span>
      </div>
  
      <div style="font-size: 26px; font-weight: bold; color: #343A76; margin-bottom: 8px; margin-top: 30px; text-align: center;">
        <span style="color: #343A76;">ROOM SUMMARY</span>
      </div>
  
      <div style="font-size: 16px; font-style: italic; color: #343A76; margin-bottom: 30px; text-align: center;">
        The Sweetest Memories Start From Here.<br/>
        And here's what they cost
      </div>
  
      <div style="width: 100%; border-top: 2px solid #343A76; margin: 20px 0;"></div>
  
      <div style="
        background-color: #fff3b0;
        border-radius: 15px;
        box-shadow: 0px 12px 20px rgba(0, 0, 0, 0.2);
        padding: 25px;
        width: 90%;
        margin: auto;
      ">
        <table style="width: 100%; border-collapse: collapse; font-size: 14px; color: #5a4a42;">
          <thead>
            <tr style="border-bottom: 1px solid #6f4e37;">
              <th style="text-align: left; padding: 8px;">SL No</th>
              <th style="text-align: left; padding: 8px;">Room Name</th>
              <th style="text-align: right; padding: 8px;">Cost Per Room</th>
            </tr>
          </thead>
          <tbody>
            ${roomSummaryData
              .map(
                (room, index) => `
                  <tr style="border-bottom: 1px solid #d3d3d3;">
                    <td style="padding: 8px;">${index + 1}</td>
                    <td style="padding: 8px;">${room.roomName}</td>
                   <td style="padding: 8px; text-align: right;">₹ ${room.cost.toLocaleString(
                     "en-IN",
                   )}</td>
</td>
                  </tr>
                `,
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;

    return div;
  };

  const createNotesPage = () => {
    const div = document.createElement("div");
    div.style.width = "210mm";
    div.style.height = "297mm";
    div.style.background = "#d3d3d3";
    div.style.padding = "15mm";
    div.style.fontFamily = "Calibri, sans-serif";
    div.style.fontSize = "14px";
    div.style.lineHeight = "1.5";

    const loginUserName =
      localStorage.getItem("userName")?.trim() || "Team SVB";

    div.innerHTML = `
    <h2 style="font-size: 20px; margin-bottom: 10px;">Notes</h2>
        <div style="background-color: #fff3b0; border: 1px solid #000; padding: 15px; border-radius: 15px;">
          <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
            <li>
              <strong>Payment Stages:</strong><br/>
              1. Stage 1: 10% Booking amount.<br/>
              2. Stage 2: 50% Before Starting of project.<br/>
              3. Stage 3: 40% Before finishing stage.
            </li><br/>
    
            <li>
              <strong>Material:</strong><br/>
              1. Plywood - ISI grade 303 - Red and White.<br/>
              2. Decorative Laminate will be used of maximum of 1500 Rs/Sheet.<br/>
              3. Light fitting as per standard make with 2 years of warranty<br/>
              4. Paint - Asian paint Royal or similar. <br/>
              5. Wire-RR or Finolex as per standard ISI graded.
            </li><br/>
    
            <li>
              <strong>General Condition:</strong><br/>
              1. Any payment made is non-refundable.<br/>
              2. Initial quote is based on primary discussion, after design stage & site visit quote may vary as per design revisions & site conditions.<br/>
              3. Item brands may vary as per product availability.<br/>
              4. Electricity required for site work should be provided by owner.<br/>
              5. Quotation is valid for 1 month only.<br/>
              6. Society approvals, municipal approvals, structural approvals, and Fire approvals are in client scope only.<br/>
              7. Softvolute Buildcon LLP not responsible for any legal approvals required for this work.<br/>
              8. If work get hold during execution for any reason, quotation is subject to revise.<br/>
              9. Material storage space needs to be provided by owners. Material storage space should be within working premises, so it ensures safety.<br/>
              10. Appliances are in client scope.
            </li><br/>
    
            <li>
              <strong>Warranty:</strong><br/>
              1. The Product warranty is - As per manufacturer warranty<br/>
              2. Bed hydraulic mechanism with 2 years of warranty as per manufacturer policy<br/>
              3. General repair warranty is for 2 years, but if found broken or damaged purposely, an extra charge will be imposed.<br/>
              4. After completion, the warranty period starts. For examination visits, a charge of Rs.500 + GST must be paid in advance.
            </li><br/>
    
            <li>
              <strong>Payment Charges:</strong><br/>
           1. Payment from <strong>credit card – 2.9%</strong> bank charges shall be extra, payment <strong>via link – 2.5%</strong> bank charges extra, and payment from <strong>debit card – 1.9%</strong> bank charges.

.<br/>
              2. Payment via QR code, UPI, NEFT and RTGS will not attract any extra charges.
            </li>
          </ul>
        </div>  
    

    
    
   <!-- Footer Section -->
         <!-- Footer Section -->
      <div style="margin-top: 40px; display: flex; justify-content: space-between; align-items: flex-start;">
        <!-- Bank Details + Sign -->
        <div style="flex: 1;">
          <strong>Bank Details</strong><br/>
          Account Name: SOFTVOLUTE BUILDCON LLP<br/>
          Bank: DBS BANK<br/>
          A/C No: 8831210000001915<br/>
          IFSC: DBSS0IN0831

          <div style="margin-top: 60px;">
            <div style="border-bottom: 1px solid black; width: 200px; margin: 8px 0;"></div>
            <strong>Customer Sign & Date</strong><br/>
            <span style="font-size: 12px;">(Email confirmation also accepted)</span>
          </div>
        </div>

        <!-- QR Codes (pushed lower) -->
        <div style="display: flex; gap: 16px; padding-top: 60px;">
          <img src="https://i.postimg.cc/bN95jw8c/QRCode.jpg" style="width: 130px;" alt="QR Code 1" />
          <img src="https://i.postimg.cc/525RbNVW/IndQr.jpg" style="width: 130px;" alt="QR Code 2" />
        </div>
      </div>

      <!-- Prepared By -->
      <div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
        <strong>Prepared by:</strong> ${loginUserName}
      </div>

    `;
    return div;
  };
  return (
    <div>
      <MdEmail
        onClick={handleSendMail}
        size={24}
        style={{
          color: "#28a745", // same green color
          cursor: "pointer",
          marginLeft: "10px",
          border: "none",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      />
    </div>
  );
};

export default EmailHelper;
